import { useEffect, useState } from "react";
import { Save, PlugZap, Trash2, Loader2, CheckCircle2, XCircle, Copy, ExternalLink, Wallet, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/components/common";
import { adminApi, errMsg, fmtDateTime } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function StatusPills({ mp }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Badge variant="outline" className={cn("border-0 ring-1", mp.active ? "bg-emerald-500/15 text-emerald-500 ring-emerald-500/30" : "bg-muted text-muted-foreground ring-border")} data-testid="mp-status-badge">
        {mp.active ? "Ativa" : "Inativa"}
      </Badge>
      {mp.configured && <Badge variant="outline" className="border-0 ring-1 ring-border" data-testid="mp-mode-badge">{mp.mode === "teste" ? "Credenciais de teste" : "Produção"}</Badge>}
    </div>
  );
}

function LastTest({ t }) {
  if (!t) return null;
  const Icon = t.ok ? CheckCircle2 : XCircle;
  return (
    <div className={cn("flex gap-3 rounded-lg p-3 text-sm", t.ok ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 text-red-600 dark:text-red-400")} data-testid="mp-last-test">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div><p className="font-medium" data-testid="mp-last-test-message">{t.message}</p><p className="text-xs opacity-80">Último teste: {fmtDateTime(t.at)}</p></div>
    </div>
  );
}

function WebhookInfo({ url }) {
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Endereço copiado"); };
  return (
    <div className="space-y-1.5 rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid="mp-webhook-info">
      <p>Aviso de pagamento: o sistema envia este endereço em cada cobrança. Não precisa configurar nada no Mercado Pago.</p>
      <div className="flex items-center gap-2"><code className="flex-1 truncate rounded bg-muted px-2 py-1 font-mono" data-testid="mp-webhook-url">{url}</code>
        <Button size="sm" variant="ghost" onClick={copy} data-testid="mp-webhook-copy-btn"><Copy className="h-3.5 w-3.5" /></Button></div>
    </div>
  );
}

function MercadoPagoCard({ data, onData }) {
  const mp = data.mercadopago;
  const [f, setF] = useState({ access_token: "", public_key: mp.public_key });
  const [busy, setBusy] = useState("");
  useEffect(() => setF({ access_token: "", public_key: mp.public_key }), [mp.public_key, mp.token_hint]);
  const run = async (kind, fn, ok) => {
    setBusy(kind);
    try { const { data: d } = await fn(); onData(d.mercadopago); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = () => run("save", () => adminApi.put("/receiving/mercadopago", { ...f, active: mp.configured ? mp.active : true }), "Mercado Pago salvo e conectado");
  const test = () => run("test", () => adminApi.post("/receiving/mercadopago/test"));
  const toggle = (v) => run("toggle", () => adminApi.patch("/receiving/mercadopago", { active: v }), v ? "Integração ativada" : "Integração desativada");
  const remove = () => window.confirm("Remover as credenciais do Mercado Pago? Pix e boleto deixam de aparecer para os clientes.") && run("remove", () => adminApi.delete("/receiving/mercadopago"), "Credenciais removidas");
  const spin = (k, Icon) => (busy === k ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icon className="mr-2 h-4 w-4" />);
  return (
    <div className="card-surface max-w-3xl space-y-5 p-6" data-testid="mp-receiving-card">
      <div className="flex flex-wrap items-start gap-4">
        <span className="grid h-11 w-11 place-items-center rounded-xl bg-sky-500/15 text-sky-500"><Wallet className="h-5 w-5" /></span>
        <div className="flex-1 min-w-[220px]">
          <p className="font-display text-lg font-bold">Mercado Pago</p>
          <p className="text-sm text-muted-foreground">Gera Pix e boleto na tela de Planos quando o teste do cliente acaba. O plano é liberado sozinho quando o pagamento é confirmado.</p>
          {mp.account && <p className="mt-1 text-xs text-muted-foreground" data-testid="mp-account">Conta conectada: <b>{mp.account}</b></p>}
        </div>
        <StatusPills mp={mp} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Access Token" hint={mp.token_hint ? `Salvo: ${mp.token_hint}. Deixe em branco para manter.` : "Começa com APP_USR- (produção) ou TEST- (teste)"}>
          <Input type="password" autoComplete="off" value={f.access_token} onChange={(e) => setF((x) => ({ ...x, access_token: e.target.value }))} placeholder={mp.token_hint || "APP_USR-..."} data-testid="mp-access-token-input" />
        </Field>
        <Field label="Public Key" hint="Da mesma conta do Access Token">
          <Input autoComplete="off" value={f.public_key} onChange={(e) => setF((x) => ({ ...x, public_key: e.target.value }))} placeholder="APP_USR-..." data-testid="mp-public-key-input" />
        </Field>
      </div>
      <a href="https://www.mercadopago.com.br/developers/panel/app" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary underline" data-testid="mp-credentials-help-link">
        Onde encontrar: Mercado Pago Developers → Suas integrações → sua aplicação → Credenciais de produção<ExternalLink className="h-3 w-3" />
      </a>
      <label className={cn("flex items-center gap-3 text-sm", !mp.configured && "opacity-50")}>
        <Switch checked={mp.active} disabled={!mp.configured || !!busy} onCheckedChange={toggle} data-testid="mp-active-switch" />Usar para cobrar os clientes (Pix e boleto)
      </label>
      <LastTest t={mp.last_test} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={!!busy} data-testid="mp-save-btn">{spin("save", Save)}Salvar</Button>
        <Button variant="outline" onClick={test} disabled={!mp.configured || !!busy} data-testid="mp-test-btn">{spin("test", PlugZap)}Testar conexão</Button>
        {mp.configured && <Button variant="ghost" className="text-red-500" onClick={remove} disabled={!!busy} data-testid="mp-remove-btn">{spin("remove", Trash2)}Remover</Button>}
      </div>
      <WebhookInfo url={data.webhook_url} />
    </div>
  );
}

export function ReceivingTab() {
  const [data, setData] = useState(null);
  useEffect(() => { adminApi.get("/receiving").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="receiving-tab">
      <div className="max-w-3xl">
        <p className="font-display text-xl font-bold">Formas de Recebimento</p>
        <p className="text-sm text-muted-foreground">Contas usadas para receber a assinatura dos seus clientes.</p>
      </div>
      <MercadoPagoCard data={data} onData={(mp) => setData((d) => ({ ...d, mercadopago: mp }))} />
      <p className="flex max-w-3xl items-center gap-2 text-xs text-muted-foreground" data-testid="receiving-card-note"><CreditCard className="h-4 w-4" />O pagamento com cartão (Stripe) continua disponível na tela de Planos.</p>
    </div>
  );
}
